import { Button, Grid, useTheme } from "@mui/material";
import { useEffect } from "react";
import Modal from "components/Common/Modal";
import useIsMobile from "hooks/useIsMobile";
import { filterAction } from "../../processor";
import { useGetCountriesQuery } from "api/api-countries";
import { useGetCommonKeysQuery } from "api/api-status";
import { modalStyling } from "./styling";
import { DataTableFilterModalProps } from "./types";
import FilterBody from "./FilterBody";
import { useFilterHeaders } from "./HeaderUtils";
import { handleFilterSubmit } from "./SubmitHandler";
import { getFilterTableInfo, getFilteredCustomColumns, getStatusDisplayValue, getStatusOptions } from "./utils";

// Add global styles for date picker to prevent dropdown issues
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  /* Make sure the datepicker is on top of everything */
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: block;
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 9999 !important;
    position: fixed !important;
  }
  
  /* Force all year-related dropdowns to proper size */
  .react-datepicker__year-dropdown-container {
    position: relative !important;
    margin-right: 5px !important;
  }
  
  /* Select element styling */
  .react-datepicker__year-select {
    width: 100% !important;
    height: 25px !important;
    padding: 2px !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    border: 1px solid #ced4da !important;
    background-color: white !important;
    font-size: 14px !important;
  }
  
  /* Contain the portal when enabled */
  .react-datepicker__portal {
    position: fixed !important;
    width: 100vw !important;
    height: 100vh !important;
    background-color: rgba(0, 0, 0, 0.5) !important;
    left: 0 !important;
    top: 0 !important;
    justify-content: center !important;
    align-items: center !important;
    display: flex !important;
    z-index: 9999 !important;
  }

  .react-datepicker__portal .react-datepicker {
    margin: 0 auto !important;
    background-color: white !important;
    border-radius: 8px !important;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2) !important;
  }
  
  /* ---- MOST IMPORTANT FIX: DISABLE THE HOVER EXPANSION ---- */
  .react-datepicker__year-option--selected_year {
    /* Ensure the selected year is clearly visible */
    font-weight: bold !important;
    background-color: #e6f7ff !important;
  }
  
  /* Disable any hover effects that cause expansion */
  .react-datepicker__year-option:hover .react-datepicker__navigation--years-upcoming,
  .react-datepicker__year-option:hover .react-datepicker__navigation--years-previous {
    display: none !important;
  }
  
  /* Hide these navigation buttons that cause expansion */
  .react-datepicker__navigation--years-upcoming,
  .react-datepicker__navigation--years-previous {
    display: none !important;
  }
`;
document.head.appendChild(styleSheet);

const FilterModal = <T extends Record<string, any> = {}>({
  filterColumnTableName,
  data,
  initialData,
  open,
  handleClose,
  setFilteredData,
  filters,
  setFilters,
  columnsToHide,
  customColumns,
  clearFilters: parentClearFilters,
  onFilterChange,
  onSubmit,
  customStatusOptions,
}: DataTableFilterModalProps<T>) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const {
    data: countries,
    isLoading: countriesLoading,
    isError: countriesError,
  } = useGetCountriesQuery();

  const countriesList = countries || [];

  const clearFilters = () => {
    setFilters({});
    setFilteredData(initialData);
    parentClearFilters();
    onFilterChange({});
  };

  const {
    data: statusData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetCommonKeysQuery();

  const { messageContainerProps, modalContentSx, modalOverlaySx, modalSx } =
    modalStyling({ isMobile, theme });

  // Get table type information
  const tableInfo = getFilterTableInfo(filterColumnTableName);

  // Get default status options based on table type
  const defaultStatusOptions = getStatusOptions(tableInfo);
  
  // Merge custom status options with default ones
  const statusOptions = customStatusOptions 
    ? { ...defaultStatusOptions, ...customStatusOptions }
    : defaultStatusOptions;

  // Get filtered custom columns based on table type
  const filteredCustomColumns = getFilteredCustomColumns(customColumns, tableInfo);

  // Get headers
  const headers = useFilterHeaders(initialData, columnsToHide, tableInfo);

  // Handle filter effects 
  useEffect(() => {
    setFilteredData(filterAction(filters, initialData));
    onFilterChange(filters);
  }, [filters, initialData, setFilteredData, onFilterChange]);

  // Handle submit
  const handleSubmit = () => {
    handleFilterSubmit({
      filters,
      filteredCustomColumns,
      statusOptions,
      tableInfo,
      initialData,
      setFilteredData,
      onSubmit
    });
    handleClose();
  };

  // Status display value helper
  const getStatusDisplayValueForKey = (statusKey: string | number | symbol, apiValue: string): string => {
    return getStatusDisplayValue(statusOptions, statusKey as keyof typeof statusOptions, apiValue);
  };

  const modalContent = (
    <FilterBody
      filters={filters}
      setFilters={setFilters}
      initialData={initialData}
      clearFilters={clearFilters}
      filteredCustomColumns={filteredCustomColumns}
      countriesList={countriesList}
      statusOptions={statusOptions}
      getStatusDisplayValue={getStatusDisplayValueForKey}
      tableInfo={tableInfo}
      headers={headers}
    />
  );

  const footerActions = (
    <Grid 
      container 
      justifyContent="flex-end" 
      spacing={2} 
      padding="1rem"
    >
      <Grid item>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        >
          Cancel
        </Button>
      </Grid>
      <Grid item>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ textTransform: "capitalize" }}
        >
          Apply
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Modal
      aria-labelledby="form-dialog-title"
      open={open}
      sx={modalSx}
      onClose={handleClose}
      width={isMobile ? "95vw" : "500px"} // Fixed width for desktop
      height={isMobile ? "80vh" : "70vh"}
      modalContentSx={{
        ...modalContentSx,
        position: 'relative',
        overflow: 'visible' // Allow dropdowns to be visible outside the modal content
      }}
      messageContainerProps={{
        ...messageContainerProps,
        sx: {
          ...(messageContainerProps.sx || {}),
          overflow: 'visible' // Allow year dropdown to be visible
        }
      }}
      modalOverlaySx={{
        ...modalOverlaySx,
        overflow: 'visible' // Allow dropdowns to be visible
      }}
      closeButton
      Header={<div style={{ padding: "1rem", fontWeight: "600", fontSize: "1.125rem" }}>Filter</div>}
      message={modalContent}
      footer={footerActions}
    />
  );
};

export default FilterModal;
